import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setActiveProduct } from '../store/slices/companySlice';
import {
  Box, Grid, Paper, Typography, useTheme,
  Alert, Button,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  ArrowForward as ArrowIcon,
  Warning as WarningIcon,
  Schedule as PendingIcon,
  MovieFilter as DCPIcon,
  Shield as ShieldIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { Link as MuiLink } from '@mui/material';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import ActiveTransfersPanel from '../components/ActiveTransfersPanel';
import { formatDashboardSubtitle } from '../utils/dashboard';
import CampaignStatistics from './CampaignStatistics';

// Live activity panel: receives data from the parent via props (the
// dashboard snapshot endpoint folds in watchers + DCP transfers in
// the same payload as queue counts so we make ONE round-trip).
//
// Renders only when the caller is admin/superadmin (the snapshot
// passes `live: null` for everyone else, so this is a no-op).
//
// Position-format helpers exported at module scope so test files
// could pull them directly if we ever wanted to lock in HH:MM:SS
// behaviour.
function fmtPosition(s) {
  if (s == null) return '';
  const sec = Math.max(0, Math.floor(s));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  if (m < 60) return `${m}m ${String(r).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m ${String(r).padStart(2, '0')}s`;
}
function fmtSince(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  return `${Math.floor(m / 60)}h ago`;
}
function fmtBytes(b) {
  if (b == null || b === 0) return '0 MB';
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(0)} MB`;
  return `${(b / 1e3).toFixed(0)} KB`;
}

function LiveActivityPanel({ live }) {
  // v10.21.57: the DCP-downloads section moved out of this panel and
  // into the shared <ActiveTransfersPanel sourceType="dcp" />, which
  // is now rendered separately on the Dashboard + Distribution pages
  // (per operator feedback: the rich progress/Mbps/ETA table belongs
  // where you act on it, not buried under the Library). What's left
  // here is the watch-sessions feed — that's a different signal (live
  // recipient viewers, not downloads) and it stays.
  const watchers = live?.watchers || [];
  if (watchers.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: '50%', bgcolor: '#43a047',
          boxShadow: '0 0 8px #43a047',
        }} />
        <Typography variant="h6">Live activity</Typography>
        <Typography variant="caption" color="text.secondary">
          {watchers.length} {watchers.length === 1 ? 'watcher' : 'watchers'} connected
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Watching screeners ({watchers.length})
            </Typography>
            {watchers.map((w) => (
              <Box
                key={w.session_id}
                sx={{
                  py: 1, borderBottom: 1, borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Typography variant="body2" fontWeight={500}>{w.recipient_name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {w.source_filename}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  At {fmtPosition(w.position_seconds)} · {Math.round(w.completion_percent)}% ·
                  started {fmtSince(w.started_at)}
                </Typography>
              </Box>
            ))}
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.35; }
              }
              `}</style>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}


// v10.4 dashboard — single snapshot endpoint, attention bar carries
// the actionable counts, product cards stay clean / steady-state.
// v10.19.10: when an active campaign is selected, the dashboard embeds
// the full Statistics page inline (stat tiles, activity timeline, bound
// DCPs + screeners rails). When nothing is selected, surfaces a single
// row inviting the operator to pick one. The standalone /statistics/:id
// route stays available for deep-linking / forwarding to the filmmaker.
function ActiveCampaignSection() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem('activeCampaignId'); } catch (_e) { return null; }
  });

  if (!activeId) {
    return (
      <Paper variant="outlined" sx={{ mt: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <CampaignIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
          No campaign selected — these are general workspace analytics. Pick a campaign to thread the dashboard with film-specific stats.
        </Typography>
        <Button size="small" variant="outlined" onClick={() => navigate('/campaigns')}>
          Choose campaign
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CampaignIcon fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
          Active campaign
        </Typography>
        <Box sx={{ flex: 1 }} />
        <MuiLink component="button" variant="caption" underline="hover"
          onClick={() => { try { localStorage.removeItem('activeCampaignId'); } catch (_e) { /* */ } setActiveId(null); }}
          sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
          Clear
        </MuiLink>
        <MuiLink component="button" variant="caption" underline="hover"
          onClick={() => navigate('/campaigns')}
          sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
          Change
        </MuiLink>
      </Box>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <CampaignStatistics campaignId={activeId} embedded />
      </Paper>
    </Box>
  );
}


function ProductChooserDashboard({
  activeCompany, hasPressReview, hasTheatricalDcp,
  pickProduct, goProduct, navigate, theme,
}) {
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Pass the operator's IANA tz so the snapshot's "today" counters
    // (completedToday, deliveredToday, failed_today) anchor on local
    // midnight instead of UTC. Pre-v10.4.35 a Pacific operator at
    // 11pm Tuesday saw Tuesday's count reset because UTC day had
    // already flipped at 4-5pm Pacific.
    const clientTz = (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
      catch (_e) { return ''; }
    })();
    const tick = async () => {
      try {
        const r = await api.get('/api/dashboard/snapshot', {
          params: clientTz ? { tz: clientTz } : undefined,
        });
        if (!cancelled) {
          setSnap(r.data);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      }
    };
    tick();
    const t = setInterval(() => { if (!document.hidden) tick(); }, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Empty defaults so the page renders before the first fetch lands.
  const wm = snap?.watermarks || { in_queue: 0, in_flight: 0, finished_today: 0,
                                   failed: 0, unread_comments: 0 };
  const dcp = snap?.dcp || { in_queue: 0, in_flight: 0, finished_today: 0,
                             syncing: 0, failed: 0, kdm_expiring: 0, kdm_expired: 0 };
  const attention = snap?.attention || [];

  // Map attention-item severity → MUI Alert severity + leading icon.
  const severityIcon = (s) => {
    if (s === 'error')   return <FailedIcon fontSize="inherit" />;
    if (s === 'warning') return <WarningIcon fontSize="inherit" />;
    return <PendingIcon fontSize="inherit" />;
  };
  const onAttentionClick = (item) => {
    if (item.action_product) {
      goProduct(item.action_product, item.action_path || '/');
    } else if (item.action_path) {
      navigate(item.action_path);
    }
  };

  return (
    <Box>
      <PageHeader
        title={activeCompany?.name || 'Workspace'}
        subtitle={formatDashboardSubtitle(snap?.subtitle)}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Attention bar — carries every actionable item. When healthy,
          we render a single positive-state row so the operator can
          tell the page is current rather than empty / broken. */}
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {attention.length === 0 ? (
          <Alert
            severity="success"
            variant="outlined"
            icon={<CompletedIcon fontSize="inherit" />}
            sx={{ py: 0.5 }}
          >
            All systems healthy — no items need your attention.
          </Alert>
        ) : (
          attention.map((item, i) => (
            <Alert
              key={`${item.kind}-${i}`}
              severity={item.severity}
              icon={severityIcon(item.severity)}
              action={
                (item.action_path || item.action_product) ? (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => onAttentionClick(item)}
                    endIcon={<ArrowIcon />}
                  >
                    View
                  </Button>
                ) : null
              }
              sx={{ py: 0.5 }}
            >
              {item.label}
            </Alert>
          ))
        )}
      </Box>

      {/* Single-product layout: cap width so the card doesn't stretch
          full-bleed on a wide monitor (looks like the layout broke). */}
      <Grid
        container
        spacing={3}
        sx={{
          mt: 1,
          ...(hasPressReview !== hasTheatricalDcp && {
            justifyContent: 'center',
          }),
        }}
      >
        {hasPressReview && (
          <Grid size={{ xs: 12, md: hasTheatricalDcp ? 6 : 8 }}>
            <ProductCard
              theme={theme}
              icon={<ShieldIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />}
              title="Watermarks"
              subtitle="Watermarked screeners for press, awards, and review"
              stats={[
                { label: 'In queue',       value: wm.in_queue },
                { label: 'In flight',      value: wm.in_flight },
                { label: 'Finished today', value: wm.finished_today },
              ]}
              onTitleClick={() => pickProduct('press_review', '/screeners')}
              actions={[
                { label: 'Create', variant: 'contained', onClick: () => pickProduct('press_review', '/screeners/create') },
                { label: 'Queue',  variant: 'outlined',  onClick: () => pickProduct('press_review', '/screeners') },
                { label: 'Distribution (per recipient)', variant: 'outlined', onClick: () => pickProduct('press_review', '/library') },
              ]}
            />
          </Grid>
        )}
        {hasTheatricalDcp && (
          <Grid size={{ xs: 12, md: hasPressReview ? 6 : 8 }}>
            <ProductCard
              theme={theme}
              icon={<DCPIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />}
              title="Theatrical DCP"
              subtitle="Digital Cinema Packages, KDMs, and theater delivery"
              stats={[
                { label: 'In queue',       value: dcp.in_queue },
                { label: 'In flight',      value: dcp.in_flight },
                { label: 'Finished today', value: dcp.finished_today },
              ]}
              onTitleClick={() => pickProduct('theatrical_dcp', '/dcp')}
              actions={[
                { label: 'Create', variant: 'contained', onClick: () => pickProduct('theatrical_dcp', '/dcp/create') },
                { label: 'Queue',  variant: 'outlined',  onClick: () => pickProduct('theatrical_dcp', '/dcp') },
                { label: 'Library (catalog)',           variant: 'outlined', onClick: () => pickProduct('theatrical_dcp', '/library') },
                { label: 'Distribution (per venue)',    variant: 'outlined', onClick: () => pickProduct('theatrical_dcp', '/distribution') },
              ]}
            />
          </Grid>
        )}
      </Grid>

      {/* v10.19.12: campaign stats sit BELOW the product tiles. The
          tiles are the operator's hot path (Create / Queue / Library);
          stats are the producer-facing rollup. Hot path first. */}
      <ActiveCampaignSection />

      {/* v10.21.57: rich in-flight transfer table. Hides itself when
          no transfers are active, so the Dashboard stays clean. */}
      <ActiveTransfersPanel sourceType="dcp" />

      {/* Live activity (admin only — backend returns null otherwise) */}
      <LiveActivityPanel live={snap?.live} />
    </Box>
  );
}

function ProductCard({ theme, icon, title, subtitle, stats, onTitleClick, actions }) {
  const accentColor = (a) => {
    if (a === 'error')   return theme.palette.error.main;
    if (a === 'warning') return theme.palette.warning.main;
    if (a === 'info')    return theme.palette.info.main;
    return undefined;
  };
  return (
    <Paper sx={{
      p: 3, borderTop: `3px solid ${theme.palette.primary.main}`,
      display: 'flex', flexDirection: 'column', gap: 2, minHeight: 260,
    }}>
      {/* Title click affordance: cursor + dotted underline on hover so
          the operator can see this header is interactive without
          having to click and find out. */}
      <Box
        onClick={onTitleClick}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
          '&:hover .product-card-title': {
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textDecorationColor: theme.palette.primary.main,
            textUnderlineOffset: '4px',
          },
        }}
      >
        {icon}
        <Box>
          <Typography variant="h5" fontWeight={700} className="product-card-title">{title}</Typography>
          <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
      </Box>

      {/* Stats grid — operator's at-a-glance read of where the
          product is right now. Highlighted when non-zero on
          actionable categories. */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {stats.map((stat, i) => (
          <Box key={i} sx={{ minWidth: 110 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: accentColor(stat.accent) || 'text.primary' }}>
              {stat.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
        {actions.map((action, i) => (
          <Button
            key={i}
            variant={action.variant}
            onClick={action.onClick}
            endIcon={<ArrowIcon />}
            size="small"
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </Paper>
  );
}

// `isToday` removed in v10.4 — the server now computes "finished_today"
// in /api/dashboard/snapshot using a UTC-midnight window, so client-side
// timezone juggling is no longer needed.

export default function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeCompanyId, list: myCompanies } = useSelector((state) => state.company);
  const activeCompany = myCompanies.find((c) => c.id === activeCompanyId);
  const hasPressReview = !!activeCompany?.has_press_review;
  const hasTheatricalDcp = !!activeCompany?.has_theatrical_dcp;

  const pickProduct = (product, destination) => {
    dispatch(setActiveProduct(product));
    navigate(destination);
  };
  const goProduct = (product, destination) => {
    dispatch(setActiveProduct(product));
    navigate(destination);
  };

  if (hasPressReview || hasTheatricalDcp) {
    return <ProductChooserDashboard
      activeCompany={activeCompany}
      hasPressReview={hasPressReview}
      hasTheatricalDcp={hasTheatricalDcp}
      pickProduct={pickProduct}
      goProduct={goProduct}
      navigate={navigate}
      theme={theme}
    />;
  }

  // Fallback: tenant has no product subscriptions.
  return <NoProductsDashboard activeCompany={activeCompany} />;
}

// Rendered for the rare "tenant has no product subscriptions yet"
// case (HHMG hasn't enabled press_review or theatrical_dcp on this
// company). The previous code path was a full-blown dashboard that
// showed jobs/dcp_jobs/etc — but those queries return empty for a
// tenant with no products, so the panels were always empty.
// Replaced with a clear "your workspace is provisional" message
// that tells the operator exactly what's missing and who to contact.
//
// FOLLOWUP: confirm whether any tenant currently sits in this state
// (no subscriptions). If none do and the policy is "every tenant is
// provisioned with at least one product", we can collapse this path
// further or remove the route entirely.
function NoProductsDashboard({ activeCompany }) {
  return (
    <Box>
      <PageHeader
        title={activeCompany?.name || 'Workspace'}
        subtitle="Your workspace is provisional — no products are enabled yet."
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        This workspace doesn't have Watermarks (press &amp; review) or
        Theatrical DCP enabled. Contact HHMG to provision the products
        you need; once enabled, they'll appear on your dashboard
        immediately.
      </Alert>
    </Box>
  );
}

// — legacy fallback dashboard (DELETED v10.4) —
// Removed in v10.4 along with the snapshot rewrite. Hooks-after-early-
// return violations (rules-of-hooks) plus dead code path: every
// active tenant has at least one product, so this code never ran.
// If you need the old layout for reference, see git history at
// commit 17d7663.
